import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { Building2, CheckCircle2, Copy, Users } from 'lucide-react';
import { Modal } from '../../../../components/common/Modal';
import { useLanguage } from '../../../../features/language/useLanguage';
import { subscriptionService } from '../../../../services/subscriptionService';
import { Th } from './primitives';

/** Read-only audit drawer showing a voucher's summary and its redemption history. */
export function VoucherDetailDrawer({ id, onClose }: { id: string; onClose: () => void }) {
  const { t } = useLanguage();
  const [copied, setCopied] = useState(false);
  const detail = useQuery({
    queryKey: ['admin', 'vouchers', id],
    queryFn: () => subscriptionService.adminVoucherDetail(id),
  });

  const handleCopy = async (code: string) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* ignore */
    }
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={t('subscription.voucher.audit_title')}
      maxWidth="2xl"
      headerAction={
        detail.data && (
          <span className="text-xs text-gray-500 font-medium tabular-nums">
            {t('subscription.voucher.audit_count', { count: detail.data.redemptions?.length ?? 0 })}
          </span>
        )
      }
    >
      {detail.isLoading && (
        <div className="px-2 py-8 text-sm text-gray-500 text-center">{t('common.loading')}</div>
      )}
      {detail.data && (
        <div className="space-y-5">
          {/* Voucher Summary */}
          <div className="rounded-lg border border-primary-200 bg-gradient-to-br from-primary-50 via-white to-white p-5">
            <p className="text-sm font-medium text-gray-900 mb-1">{detail.data.voucher.name}</p>
            <button
              onClick={() => handleCopy(detail.data!.voucher.code)}
              className="group inline-flex items-center gap-2 mb-4"
              title="Copy code"
            >
              <span className="font-mono text-2xl font-bold tracking-wider text-primary-700">
                {detail.data.voucher.code}
              </span>
              <span className="text-gray-400 group-hover:text-primary-600 transition-colors">
                {copied ? (
                  <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
              </span>
            </button>

            <div className="grid grid-cols-3 gap-3 text-xs">
              <div>
                <div className="uppercase tracking-wider text-gray-500 font-medium mb-0.5">
                  {t('subscription.voucher.col.duration')}
                </div>
                <div className="text-sm font-semibold text-gray-900 tabular-nums">
                  {detail.data.voucher.duration_months} {detail.data.voucher.duration_months === 1 ? 'Month' : 'Months'}
                </div>
              </div>
              <div>
                <div className="uppercase tracking-wider text-gray-500 font-medium mb-0.5">
                  {t('subscription.voucher.col.expires')}
                </div>
                <div className="text-sm font-semibold text-gray-900 tabular-nums">
                  {format(new Date(detail.data.voucher.expires_at), 'PP')}
                </div>
              </div>
              <div>
                <div className="uppercase tracking-wider text-gray-500 font-medium mb-0.5">
                  {t('subscription.voucher.col.usage')}
                </div>
                <div className="text-sm font-semibold text-gray-900 tabular-nums">
                  {detail.data.voucher.redemption_count}
                  <span className="text-gray-400 font-normal">
                    {' / '}
                    {detail.data.voucher.max_redemptions !== null
                      ? detail.data.voucher.max_redemptions
                      : '∞'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Redemptions — table layout scales gracefully past a handful of rows. */}
          {(detail.data.redemptions?.length ?? 0) === 0 ? (
            <div className="text-center py-8">
              <div className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-gray-100 mb-2">
                <Users className="h-5 w-5 text-gray-400" />
              </div>
              <p className="text-sm text-gray-500">{t('subscription.voucher.audit_empty')}</p>
            </div>
          ) : (
            <div className="rounded-lg border border-gray-200 overflow-hidden">
              <div className="max-h-[50vh] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 sticky top-0 z-10">
                    <tr className="text-left">
                      <Th>{t('subscription.voucher.audit.company', undefined, 'Company')}</Th>
                      <Th>{t('subscription.voucher.audit.redeemed_at', undefined, 'Redeemed')}</Th>
                      <Th>{t('subscription.voucher.audit.extended_to', undefined, 'Extended Until')}</Th>
                      <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                        {t('subscription.voucher.audit.months', undefined, 'Months')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {detail.data.redemptions?.map((r) => {
                      const companyLabel = r.company_name?.trim() || `${r.company_id.slice(0, 8)}…`;
                      const hasName = Boolean(r.company_name?.trim());
                      return (
                        <tr key={r.id} className="hover:bg-gray-50/60 transition-colors">
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <span
                                className={`flex h-6 w-6 shrink-0 items-center justify-center rounded ${
                                  hasName ? 'bg-primary-50 text-primary-700' : 'bg-gray-100 text-gray-400'
                                }`}
                              >
                                <Building2 className="h-3 w-3" />
                              </span>
                              <span
                                className={
                                  hasName ? 'font-medium text-gray-900' : 'font-mono text-[11px] text-gray-500'
                                }
                                title={r.company_id}
                              >
                                {companyLabel}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-xs text-gray-600 tabular-nums whitespace-nowrap">
                            {format(new Date(r.redeemed_at), 'PP p')}
                          </td>
                          <td className="px-4 py-3 text-xs text-gray-600 tabular-nums whitespace-nowrap">
                            {format(new Date(r.subscription_extended_to), 'PP')}
                          </td>
                          <td className="px-4 py-3 text-right whitespace-nowrap">
                            <span className="inline-flex items-center rounded bg-primary-50 text-primary-700 px-1.5 py-0.5 text-[11px] font-semibold tabular-nums">
                              +{r.months_added} mo
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}
